import type { VideoCall } from '@/types'
import type {
  BeautyApplyStatus,
  BeautyCapability,
  BeautySettings,
} from '@/types/beauty'
import { callApi } from './callApi'
import type {
  IAgoraRTCClient,
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
  IRemoteAudioTrack,
  IRemoteVideoTrack,
} from 'agora-rtc-sdk-ng'

type MediaType = 'audio' | 'video' | 'datachannel'
type FacingMode = 'user' | 'environment'
type AgoraRTCModule = (typeof import('agora-rtc-sdk-ng'))['default']
type ActiveTokenRenewHandler = (call: VideoCall) => Promise<VideoCall | null>
type RemoteUserLeftListener = (call: VideoCall) => void
type BeautyStateListener = () => void
type SDKReadyListener = () => void
export type SDKLoadState = 'idle' | 'loading' | 'ready' | 'failed'
export type LocalMediaFailureReason =
  | 'NO_MICROPHONE'
  | 'NO_CAMERA'
  | 'PERMISSION_DENIED'
  | 'DEVICE_NOT_FOUND'
  | 'UNKNOWN'
export type LocalMediaPreparationResult =
  | { ok: true }
  | { ok: false; reason: LocalMediaFailureReason; error?: unknown }
export interface LocalMediaPreparationOptions {
  owner?: string
}
export interface LocalVideoFrameCapture {
  imageBase64: string
  width: number
  height: number
}
type BeautyOverloadTrack = ICameraVideoTrack & {
  on(event: 'beauty-effect-overload', listener: () => void): void
}

let cachedAgoraRTC: AgoraRTCModule | null = null

export interface CallMediaState {
  connected: boolean
  joining: boolean
  localAudioEnabled: boolean
  localVideoEnabled: boolean
  remoteAudioEnabled: boolean
  remoteVideoAvailable: boolean
  remoteUserJoined: boolean
  switchingCamera: boolean
}

export type InitialCallMediaPreferences = Partial<
  Pick<
    CallMediaState,
    'localAudioEnabled' | 'localVideoEnabled' | 'remoteAudioEnabled'
  >
>

export interface BeautyRuntimeState {
  capability: BeautyCapability
  applyStatus: BeautyApplyStatus
  message: string | null
}

interface ActiveCallState {
  call: VideoCall
  renewTimer: number | null
  onRenew: ActiveTokenRenewHandler
}

const defaultCallMediaState: CallMediaState = {
  connected: false,
  joining: false,
  localAudioEnabled: true,
  localVideoEnabled: true,
  remoteAudioEnabled: true,
  remoteVideoAvailable: false,
  remoteUserJoined: false,
  switchingCamera: false,
}

const defaultBeautyState: BeautyRuntimeState = {
  capability: 'unknown',
  applyStatus: 'idle',
  message: null,
}

class BrowserShengwangClient {
  private activeCall: ActiveCallState | null = null
  private activeClient: IAgoraRTCClient | null = null
  private localAudioTrack: IMicrophoneAudioTrack | null = null
  private localVideoTrack: ICameraVideoTrack | null = null
  private localAudioPublished = false
  private localVideoPublished = false
  private remoteAudioTrack: IRemoteAudioTrack | null = null
  private remoteVideoTrack: IRemoteVideoTrack | null = null
  private localVideoContainer: HTMLElement | null = null
  private remoteVideoContainer: HTMLElement | null = null
  private listeners = new Set<() => void>()
  private remoteUserLeftListeners = new Set<RemoteUserLeftListener>()
  private beautyStateListeners = new Set<BeautyStateListener>()
  private sdkReadyListeners = new Set<SDKReadyListener>()
  private mediaState: CallMediaState = { ...defaultCallMediaState }
  private beautyState: BeautyRuntimeState = { ...defaultBeautyState }
  private beautySettings: BeautySettings | null = null
  private beautyApplyVersion = 0
  private beautyOverloadTrack: ICameraVideoTrack | null = null
  private facingMode: FacingMode = 'user'
  private localTracksOwner: string | null = null
  private sdkLoadState: SDKLoadState = 'idle'
  private sdkModule: AgoraRTCModule | null = null
  private sdkPreloadPromise: Promise<AgoraRTCModule> | null = null

  preloadSDK() {
    if (this.sdkModule) {
      return Promise.resolve(this.sdkModule)
    }
    if (!this.sdkPreloadPromise) {
      this.patchSDKLoadState('loading')
      this.sdkPreloadPromise = loadAgoraRTC()
        .then((module) => {
          this.sdkModule = module
          this.patchSDKLoadState('ready')
          return module
        })
        .catch((error) => {
          this.sdkPreloadPromise = null
          this.patchSDKLoadState('failed')
          throw error
        })
    }
    return this.sdkPreloadPromise
  }

  isSDKReady() {
    return this.sdkLoadState === 'ready'
  }

  getSDKLoadState() {
    return this.sdkLoadState
  }

  async prepareLocalTracks(options: LocalMediaPreparationOptions = {}) {
    if (this.localAudioTrack && this.localVideoTrack) {
      if (options.owner) {
        this.localTracksOwner = options.owner
      }
      return
    }
    this.closeLocalTracks()
    const AgoraRTC = this.sdkModule || (await this.preloadSDK())
    try {
      const [audioTrack, videoTrack] =
        await AgoraRTC.createMicrophoneAndCameraTracks(
          {},
          {
            facingMode: this.facingMode,
            encoderConfig: '480p_1',
          },
        )
      this.localAudioTrack = audioTrack
      this.localVideoTrack = videoTrack
      this.localTracksOwner = options.owner || null
      this.patchMediaState({
        localAudioEnabled: true,
        localVideoEnabled: true,
      })
    } catch (error) {
      this.closeLocalTracks()
      throw error
    }
  }

  async prepareLocalTracksForAccept(
    options: LocalMediaPreparationOptions = {},
  ): Promise<LocalMediaPreparationResult> {
    try {
      await this.prepareLocalTracks(options)
      return { ok: true }
    } catch (error) {
      return {
        ok: false,
        reason: await resolveLocalMediaFailureReason(error),
        error,
      }
    }
  }

  async enterCall(
    call: VideoCall,
    initialMediaPreferences: InitialCallMediaPreferences = {},
  ) {
    if (this.activeClient || this.activeCall) {
      await this.leaveCall()
    }
    const channel = call.channel_name || call.rtc_channel || ''
    if (!call.app_id || !channel || !call.rtc_token || !call.uid) {
      await this.leaveCall()
      throw new Error('Missing Shengwang call parameters')
    }

    const AgoraRTC = await loadAgoraRTC()
    this.patchMediaState({ joining: true })
    try {
      this.logRTCJoinAttempt(call, channel)
      await this.prepareLocalTracks()
      const localAudioEnabled =
        initialMediaPreferences.localAudioEnabled ?? true
      const localVideoEnabled =
        initialMediaPreferences.localVideoEnabled ?? true
      const remoteAudioEnabled =
        initialMediaPreferences.remoteAudioEnabled ?? true
      await this.localAudioTrack?.setEnabled(localAudioEnabled)
      await this.localVideoTrack?.setEnabled(localVideoEnabled)
      this.activeClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' })
      this.registerActiveClientHandlers(this.activeClient)
      await this.activeClient.join(
        call.app_id,
        channel,
        call.rtc_token,
        call.uid,
      )
      const tracksToPublish = [
        localAudioEnabled ? this.localAudioTrack : null,
        localVideoEnabled ? this.localVideoTrack : null,
      ].filter(Boolean) as Array<IMicrophoneAudioTrack | ICameraVideoTrack>
      if (tracksToPublish.length > 0) {
        await this.activeClient.publish(tracksToPublish)
      }
      this.localAudioPublished = Boolean(
        localAudioEnabled && this.localAudioTrack,
      )
      this.localVideoPublished = Boolean(
        localVideoEnabled && this.localVideoTrack,
      )
      this.activeCall = {
        call,
        renewTimer: null,
        onRenew: defaultActiveCallRenew,
      }
      this.patchMediaState({
        connected: true,
        joining: false,
        localAudioEnabled,
        localVideoEnabled,
        remoteAudioEnabled,
      })
      this.scheduleActiveRenewal()
      this.playLocalVideo()
      if (localVideoEnabled) {
        void this.applyBeautySettingsBestEffort()
      } else {
        this.patchBeautyState({
          applyStatus: 'unavailable',
          message: '未进入通话、无视频轨道或摄像头已关闭',
        })
      }
    } catch (error) {
      this.logRTCJoinFailure(call, channel, error)
      await this.leaveCall()
      throw error
    }
  }

  async leaveCall() {
    const client = this.activeClient
    const active = this.activeCall
    const remoteAudioTrack = this.remoteAudioTrack
    const remoteVideoTrack = this.remoteVideoTrack
    const localAudioTrack = this.localAudioTrack
    const localVideoTrack = this.localVideoTrack
    const localTracks = [this.localAudioTrack, this.localVideoTrack].filter(
      Boolean,
    ) as Array<IMicrophoneAudioTrack | ICameraVideoTrack>

    this.clearActiveRenewal()
    if (this.activeClient === client) {
      this.activeClient = null
    }
    this.localAudioPublished = false
    this.localVideoPublished = false
    if (this.activeCall === active) {
      this.activeCall = null
    }
    remoteAudioTrack?.stop()
    remoteVideoTrack?.stop()
    if (this.remoteAudioTrack === remoteAudioTrack) {
      this.remoteAudioTrack = null
    }
    if (this.remoteVideoTrack === remoteVideoTrack) {
      this.remoteVideoTrack = null
    }
    localAudioTrack?.stop()
    localVideoTrack?.stop()
    if (this.localAudioTrack === localAudioTrack) {
      this.localAudioTrack = null
    }
    if (this.localVideoTrack === localVideoTrack) {
      this.localVideoTrack = null
    }
    if (client && localTracks.length) {
      await client.unpublish(localTracks).catch(() => undefined)
    }
    await client?.leave().catch(() => undefined)
    this.closeLocalTracks(localAudioTrack, localVideoTrack)
    if (this.activeClient || this.activeCall) {
      return
    }
    this.localVideoContainer = null
    this.remoteVideoContainer = null
    this.mediaState = { ...defaultCallMediaState }
    this.beautyApplyVersion++
    this.beautyOverloadTrack = null
    this.beautyState = { ...defaultBeautyState, applyStatus: 'unavailable' }
    this.emitChange()
    this.emitBeautyChange()
  }

  async leaveCallForOrder(orderID: string) {
    const activeOrderID = this.activeCall?.call.order_id || ''
    if (activeOrderID && activeOrderID === orderID) {
      await this.leaveCall()
      return
    }
    this.closePreparedLocalTracksIfIdle(`outgoing:${orderID}`)
  }

  closePreparedLocalTracksIfIdle(owner?: string) {
    if (this.activeClient || this.activeCall) {
      return
    }
    if (owner && this.localTracksOwner !== owner) {
      return
    }
    this.localAudioTrack?.stop()
    this.localVideoTrack?.stop()
    this.closeLocalTracks()
    this.patchMediaState({
      localAudioEnabled: true,
      localVideoEnabled: true,
    })
  }

  emergencyCloseMedia() {
    this.remoteAudioTrack?.stop()
    this.remoteVideoTrack?.stop()
    this.remoteAudioTrack = null
    this.remoteVideoTrack = null
    this.localAudioPublished = false
    this.localVideoPublished = false
    this.localAudioTrack?.stop()
    this.localVideoTrack?.stop()
    this.closeLocalTracks()
    this.localVideoContainer = null
    this.remoteVideoContainer = null
    this.mediaState = { ...defaultCallMediaState }
    this.beautyApplyVersion++
    this.beautyOverloadTrack = null
    this.beautyState = { ...defaultBeautyState, applyStatus: 'unavailable' }
    this.emitChange()
    this.emitBeautyChange()
  }

  getActiveCall() {
    return this.activeCall?.call || null
  }

  getMediaState() {
    return this.mediaState
  }

  captureLocalVideoFrame(
    maxWidth = 480,
    quality = 0.72,
  ): LocalVideoFrameCapture | null {
    if (
      !this.localVideoContainer ||
      !this.localVideoTrack ||
      !this.mediaState.localVideoEnabled
    ) {
      return null
    }
    const video = this.localVideoContainer.querySelector('video')
    if (!video || video.videoWidth <= 0 || video.videoHeight <= 0) {
      return null
    }
    const scale = Math.min(1, maxWidth / video.videoWidth)
    const width = Math.max(1, Math.round(video.videoWidth * scale))
    const height = Math.max(1, Math.round(video.videoHeight * scale))
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) {
      return null
    }
    context.drawImage(video, 0, 0, width, height)
    return {
      imageBase64: canvas.toDataURL('image/jpeg', quality),
      width,
      height,
    }
  }

  getBeautyState() {
    return this.beautyState
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  subscribeRemoteUserLeft(listener: RemoteUserLeftListener) {
    this.remoteUserLeftListeners.add(listener)
    return () => {
      this.remoteUserLeftListeners.delete(listener)
    }
  }

  subscribeBeautyState(listener: BeautyStateListener) {
    this.beautyStateListeners.add(listener)
    return () => {
      this.beautyStateListeners.delete(listener)
    }
  }

  subscribeSDKReady(listener: SDKReadyListener) {
    this.sdkReadyListeners.add(listener)
    return () => {
      this.sdkReadyListeners.delete(listener)
    }
  }

  setBeautySettings(settings: BeautySettings) {
    this.beautySettings = settings
    if (!isBeautySupportedEnvironment()) {
      this.patchBeautyState({
        capability: 'unsupported',
        applyStatus: 'failed',
        message: '当前设备暂不支持美颜',
      })
      return
    }
    if (
      !this.activeClient ||
      !this.localVideoTrack ||
      !this.mediaState.localVideoEnabled
    ) {
      this.patchBeautyState({
        applyStatus: 'unavailable',
        message: '未进入通话、无视频轨道或摄像头已关闭',
      })
      return
    }
    void this.applyBeautySettingsBestEffort(settings)
  }

  async applyBeautySettingsBestEffort(settings = this.beautySettings) {
    const track = this.localVideoTrack
    const version = ++this.beautyApplyVersion
    if (!settings) {
      return
    }
    if (!isBeautySupportedEnvironment()) {
      this.patchBeautyState({
        capability: 'unsupported',
        applyStatus: 'failed',
        message: '当前设备暂不支持美颜',
      })
      return
    }
    if (!this.activeClient || !track || !this.mediaState.localVideoEnabled) {
      this.patchBeautyState({
        applyStatus: 'unavailable',
        message: '未进入通话、无视频轨道或摄像头已关闭',
      })
      return
    }
    if (this.beautyState.capability === 'overloaded' && settings.enabled) {
      this.patchBeautyState({
        applyStatus: 'failed',
        message: '设备性能不足，已关闭美颜',
      })
      return
    }
    this.registerBeautyOverloadHandler(track)
    const overloaded = this.beautyState.capability === 'overloaded'
    this.patchBeautyState({
      capability: overloaded ? 'overloaded' : 'supported',
      applyStatus: 'applying',
      message: overloaded ? '设备性能不足，已关闭美颜' : null,
    })
    try {
      await track.setBeautyEffect(settings.enabled, {
        lighteningLevel: settings.lightening_level,
        smoothnessLevel: settings.smoothness_level,
        rednessLevel: settings.redness_level,
      })
      if (
        this.localVideoTrack !== track ||
        this.beautyApplyVersion !== version
      ) {
        return
      }
      this.patchBeautyState({
        capability: overloaded ? 'overloaded' : 'supported',
        applyStatus: overloaded
          ? 'failed'
          : settings.enabled
            ? 'applied'
            : 'idle',
        message: overloaded
          ? '设备性能不足，已关闭美颜'
          : settings.enabled
            ? '已应用'
            : null,
      })
    } catch {
      if (
        this.localVideoTrack !== track ||
        this.beautyApplyVersion !== version
      ) {
        return
      }
      this.patchBeautyState({
        capability: 'unsupported',
        applyStatus: 'failed',
        message: '当前设备暂不支持美颜',
      })
    }
  }

  setLocalVideoContainer(container: HTMLElement | null) {
    this.localVideoContainer = container
    this.playLocalVideo()
  }

  setRemoteVideoContainer(container: HTMLElement | null) {
    this.remoteVideoContainer = container
    this.playRemoteVideo()
  }

  async setLocalAudioEnabled(enabled: boolean) {
    if (!this.localAudioTrack) {
      return
    }
    await this.localAudioTrack.setEnabled(enabled)
    if (enabled && this.activeClient && !this.localAudioPublished) {
      await this.activeClient.publish(this.localAudioTrack)
      this.localAudioPublished = true
    }
    this.patchMediaState({ localAudioEnabled: enabled })
  }

  async setLocalVideoEnabled(enabled: boolean) {
    if (!this.localVideoTrack) {
      return
    }
    await this.localVideoTrack.setEnabled(enabled)
    if (enabled && this.activeClient && !this.localVideoPublished) {
      await this.activeClient.publish(this.localVideoTrack)
      this.localVideoPublished = true
    }
    this.patchMediaState({ localVideoEnabled: enabled })
    if (enabled) {
      this.playLocalVideo()
      void this.applyBeautySettingsBestEffort()
    } else {
      this.patchBeautyState({
        applyStatus: 'unavailable',
        message: '未进入通话、无视频轨道或摄像头已关闭',
      })
    }
  }

  setRemoteAudioEnabled(enabled: boolean) {
    if (enabled) {
      this.remoteAudioTrack?.play()
    } else {
      this.remoteAudioTrack?.stop()
    }
    this.patchMediaState({ remoteAudioEnabled: enabled })
  }

  async switchCamera() {
    if (!this.activeClient || !this.localVideoTrack) {
      return
    }
    const nextFacingMode: FacingMode =
      this.facingMode === 'user' ? 'environment' : 'user'
    this.patchMediaState({ switchingCamera: true })
    const AgoraRTC = await loadAgoraRTC()
    let nextTrack: ICameraVideoTrack | null = null
    const previousTrack = this.localVideoTrack
    const wasVideoPublished = this.localVideoPublished
    let previousUnpublished = false
    try {
      nextTrack = await AgoraRTC.createCameraVideoTrack({
        facingMode: nextFacingMode,
        encoderConfig: '480p_1',
      })
      if (wasVideoPublished) {
        await this.activeClient.unpublish(previousTrack)
        this.localVideoPublished = false
        previousUnpublished = true
      }
      this.localVideoTrack = nextTrack
      if (wasVideoPublished) {
        await this.activeClient.publish(nextTrack)
        this.localVideoPublished = true
      }
      previousTrack.stop()
      previousTrack.close()
      this.facingMode = nextFacingMode
      this.playLocalVideo()
      void this.applyBeautySettingsBestEffort()
      nextTrack = null
    } catch (error) {
      if (this.localVideoTrack !== previousTrack) {
        nextTrack?.close()
        this.localVideoTrack = previousTrack
      }
      if (previousUnpublished) {
        await this.activeClient
          ?.publish(previousTrack)
          .then(() => {
            this.localVideoPublished = true
          })
          .catch(() => undefined)
      }
      this.playLocalVideo()
      throw error
    } finally {
      nextTrack?.close()
      this.patchMediaState({ switchingCamera: false })
    }
  }

  private scheduleActiveRenewal() {
    this.clearActiveRenewal()
    if (!this.activeCall || !this.activeCall.call.expire_at) {
      return
    }

    const now = Math.floor(Date.now() / 1000)
    const renewInSeconds = Math.max(
      this.activeCall.call.expire_at - now - 30,
      5,
    )
    this.activeCall.renewTimer = window.setTimeout(async () => {
      const active = this.activeCall
      if (!active || !this.activeClient) {
        return
      }
      const nextCall = await active.onRenew(active.call).catch(() => null)
      if (
        !nextCall?.rtc_token ||
        this.activeCall !== active ||
        !this.activeClient
      ) {
        this.scheduleActiveRenewal()
        return
      }
      const renewed = await this.activeClient
        .renewToken(nextCall.rtc_token)
        .then(
          () => true,
          () => false,
        )
      if (!renewed) {
        this.scheduleActiveRenewal()
        return
      }
      if (this.activeCall !== active || !this.activeClient) {
        return
      }
      this.activeCall.call = {
        ...active.call,
        ...nextCall,
      }
      this.scheduleActiveRenewal()
    }, renewInSeconds * 1000)
  }

  private clearActiveRenewal() {
    const active = this.activeCall
    if (active && active.renewTimer !== null) {
      window.clearTimeout(active.renewTimer)
      active.renewTimer = null
    }
  }

  private registerActiveClientHandlers(client: IAgoraRTCClient) {
    client.on('user-joined', (user) => {
      if (this.activeClient !== client) {
        return
      }
      this.logRTCEvent('user-joined', {
        uid: String(user.uid),
      })
      this.patchMediaState({ remoteUserJoined: true })
    })
    client.on('user-published', (user, mediaType) => {
      this.logRTCEvent('user-published', {
        uid: String(user.uid),
        mediaType,
        hasAudioTrack: Boolean(user.audioTrack),
        hasVideoTrack: Boolean(user.videoTrack),
      })
      void this.handleUserPublished(user, mediaType)
    })
    client.on('user-unpublished', (user, mediaType) => {
      this.logRTCEvent('user-unpublished', {
        uid: String(user.uid),
        mediaType,
      })
      this.handleUserUnpublished(mediaType)
    })
    client.on('user-left', (user) => {
      if (this.activeClient !== client) {
        return
      }
      this.logRTCEvent('user-left', {
        uid: String(user.uid),
      })
      const activeCall = this.activeCall?.call || null
      this.remoteAudioTrack?.stop()
      this.remoteVideoTrack?.stop()
      this.remoteAudioTrack = null
      this.remoteVideoTrack = null
      this.patchMediaState({
        remoteUserJoined: false,
        remoteVideoAvailable: false,
      })
      if (activeCall) {
        this.emitRemoteUserLeft(activeCall)
      }
    })
  }

  private async handleUserPublished(
    user: IAgoraRTCRemoteUser,
    mediaType: MediaType,
  ) {
    if (!this.activeClient || mediaType === 'datachannel') {
      return
    }
    try {
      await this.activeClient.subscribe(user, mediaType)
      this.logRTCEvent('subscribe-succeeded', {
        uid: String(user.uid),
        mediaType,
        hasAudioTrack: Boolean(user.audioTrack),
        hasVideoTrack: Boolean(user.videoTrack),
      })
    } catch (error) {
      this.logRTCEvent('subscribe-failed', {
        uid: String(user.uid),
        mediaType,
        error: errorMessage(error),
      })
      throw error
    }
    this.patchMediaState({ remoteUserJoined: true })
    if (mediaType === 'audio' && user.audioTrack) {
      this.remoteAudioTrack = user.audioTrack
      if (this.mediaState.remoteAudioEnabled) {
        this.remoteAudioTrack.play()
      }
      return
    }
    if (mediaType === 'video' && user.videoTrack) {
      this.remoteVideoTrack = user.videoTrack
      this.patchMediaState({ remoteVideoAvailable: true })
      this.playRemoteVideo()
    }
  }

  private logRTCEvent(event: string, fields: Record<string, unknown> = {}) {
    const call = this.activeCall?.call
    console.log('[rtc]', event, {
      orderID: call?.order_id || '',
      direction: call?.direction || '',
      channel: call?.channel_name || call?.rtc_channel || '',
      ...fields,
    })
  }

  private logRTCJoinAttempt(call: VideoCall, channel: string) {
    this.logRTCEvent('join-attempt', {
      appID: call.app_id,
      channel,
      uid: call.uid,
      expireAt: call.expire_at,
      now: Math.floor(Date.now() / 1000),
      hasToken: Boolean(call.rtc_token),
    })
  }

  private logRTCJoinFailure(call: VideoCall, channel: string, error: unknown) {
    this.logRTCEvent('join-failed', {
      appID: call.app_id,
      channel,
      uid: call.uid,
      expireAt: call.expire_at,
      now: Math.floor(Date.now() / 1000),
      hasToken: Boolean(call.rtc_token),
      error: errorMessage(error),
    })
  }

  private handleUserUnpublished(mediaType: MediaType) {
    if (mediaType === 'audio') {
      this.remoteAudioTrack?.stop()
      this.remoteAudioTrack = null
    }
    if (mediaType === 'video') {
      this.remoteVideoTrack?.stop()
      this.remoteVideoTrack = null
      this.patchMediaState({ remoteVideoAvailable: false })
    }
  }

  private playLocalVideo() {
    if (
      !this.localVideoContainer ||
      !this.localVideoTrack ||
      !this.mediaState.localVideoEnabled
    ) {
      return
    }
    this.localVideoTrack.play(this.localVideoContainer, { fit: 'cover' })
  }

  private registerBeautyOverloadHandler(track: ICameraVideoTrack) {
    if (this.beautyOverloadTrack === track) {
      return
    }
    this.beautyOverloadTrack = track
    ;(track as BeautyOverloadTrack).on('beauty-effect-overload', () => {
      if (this.localVideoTrack !== track) {
        return
      }
      this.beautyApplyVersion++
      void track.setBeautyEffect(false).catch(() => undefined)
      this.patchBeautyState({
        capability: 'overloaded',
        applyStatus: 'failed',
        message: '设备性能不足，已关闭美颜',
      })
    })
  }

  private playRemoteVideo() {
    if (!this.remoteVideoContainer || !this.remoteVideoTrack) {
      return
    }
    this.remoteVideoTrack.play(this.remoteVideoContainer, { fit: 'cover' })
  }

  private closeLocalTracks(
    audioTrack: IMicrophoneAudioTrack | null = this.localAudioTrack,
    videoTrack: ICameraVideoTrack | null = this.localVideoTrack,
  ) {
    audioTrack?.close()
    videoTrack?.close()
    if (this.localAudioTrack === audioTrack) {
      this.localAudioTrack = null
      this.localAudioPublished = false
    }
    if (this.localVideoTrack === videoTrack) {
      this.localVideoTrack = null
      this.localVideoPublished = false
    }
    if (!this.localAudioTrack && !this.localVideoTrack) {
      this.localTracksOwner = null
    }
  }

  private patchMediaState(patch: Partial<CallMediaState>) {
    this.mediaState = { ...this.mediaState, ...patch }
    this.emitChange()
  }

  private patchBeautyState(patch: Partial<BeautyRuntimeState>) {
    this.beautyState = { ...this.beautyState, ...patch }
    this.emitBeautyChange()
  }

  private patchSDKLoadState(state: SDKLoadState) {
    if (this.sdkLoadState === state) {
      return
    }
    this.sdkLoadState = state
    this.emitSDKReadyChange()
  }

  private emitChange() {
    this.listeners.forEach((listener) => listener())
  }

  private emitRemoteUserLeft(call: VideoCall) {
    this.remoteUserLeftListeners.forEach((listener) => listener(call))
  }

  private emitBeautyChange() {
    this.beautyStateListeners.forEach((listener) => listener())
  }

  private emitSDKReadyChange() {
    this.sdkReadyListeners.forEach((listener) => listener())
  }
}

export const shengwangClient = new BrowserShengwangClient()

export function normalizeLocalMediaError(
  error: unknown,
): LocalMediaFailureReason {
  const value = error as { code?: unknown; name?: unknown; message?: unknown }
  const code = String(value?.code || value?.name || '')
  const message = String(value?.message || '')
  const normalized = `${code} ${message}`.toLowerCase()

  if (
    normalized.includes('notallowed') ||
    normalized.includes('permission_denied') ||
    normalized.includes('permission denied')
  ) {
    return 'PERMISSION_DENIED'
  }
  if (
    normalized.includes('device_not_found') ||
    normalized.includes('notfound')
  ) {
    return 'DEVICE_NOT_FOUND'
  }
  if (normalized.includes('microphone') || normalized.includes('audio input')) {
    return 'NO_MICROPHONE'
  }
  if (normalized.includes('camera') || normalized.includes('video input')) {
    return 'NO_CAMERA'
  }
  return 'UNKNOWN'
}

async function resolveLocalMediaFailureReason(
  error: unknown,
): Promise<LocalMediaFailureReason> {
  const normalized = normalizeLocalMediaError(error)
  if (normalized !== 'DEVICE_NOT_FOUND' && normalized !== 'UNKNOWN') {
    return normalized
  }
  try {
    const AgoraRTC = await loadAgoraRTC()
    const [microphones, cameras] = await Promise.all([
      AgoraRTC.getMicrophones(),
      AgoraRTC.getCameras(),
    ])
    if (!microphones.length) {
      return 'NO_MICROPHONE'
    }
    if (!cameras.length) {
      return 'NO_CAMERA'
    }
  } catch {
    return normalized
  }
  return normalized
}

async function defaultActiveCallRenew(call: VideoCall) {
  const orderID = call.order_id || ''
  if (!orderID) {
    return null
  }
  const channelName = call.channel_name || call.rtc_channel || undefined
  const response = await callApi.renewCallToken(orderID, {
    channel_name: channelName,
  })
  return response.data || null
}

async function loadAgoraRTC() {
  if (cachedAgoraRTC) {
    return cachedAgoraRTC
  }
  const module = await import('agora-rtc-sdk-ng')
  cachedAgoraRTC = module.default
  return cachedAgoraRTC
}

function isBeautySupportedEnvironment() {
  if (typeof navigator === 'undefined') {
    return true
  }
  const userAgent = navigator.userAgent || ''
  return !/Android|iPhone|iPad|iPod|Mobile/i.test(userAgent)
}

function errorMessage(error: unknown) {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`
  }
  return String(error)
}
